import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Document, DocumentType } from '@proref/types';
import { useAuth } from '../contexts/auth-context';
import { useDocumentsQuery, useDownloadDocument, useDeleteDocument } from '../hooks/use-documents';
import { useToggleDocumentBookmark } from '../hooks/use-bookmarks';
import { notifyError, notifySuccess } from '../lib/toast';
import { PageHeader } from '../components/shared/PageHeader';
import { Skeleton } from '../components/shared/Skeleton';
import { Button } from '../components/shared/Button';
import { Icon } from '../components/shared/Icons';
import { UploadDocumentDialog } from './admin/UploadDocumentDialog';

// Per-type pill tint so the library scans at a glance. Keyed to the closed document_type set. A
// function (not a module constant) since the labels are translated — takes `t` like every other
// plain (non-component) helper in this codebase.
function typeMeta(t: TFunction): Record<DocumentType, { label: string; tint: string }> {
  return {
    rulebook: { label: t('documents.typeRulebook'), tint: 'bg-sky-100 text-sky-700' },
    commentary: { label: t('documents.typeCommentary'), tint: 'bg-violet-100 text-violet-700' },
    signal_code: { label: t('documents.typeSignalCode'), tint: 'bg-amber-100 text-amber-700' },
    template: { label: t('documents.typeTemplate'), tint: 'bg-teal-100 text-teal-700' },
  };
}

function formatSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// The referee/admin document library. Reference files (rulebooks, signal codes, templates) grouped
// into Official (platform, federation_id null — ProRef-authored, sport-wide) and Your federation
// (this federation's own uploads). Admins get an Upload button + per-doc delete; referees download.
export function DocumentsPage() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isAdmin = role === 'super_admin' || role === 'federation_admin';
  // Bookmarking is a referee/federation_admin action (super_admin has no personal library).
  const canBookmark = role === 'referee' || role === 'federation_admin';
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useDocumentsQuery();

  // Split into platform (official) vs federation-owned. super_admin sees every federation's docs;
  // the grouping still reads clearly as "official vs federation-authored".
  const { platform, federation } = useMemo(() => {
    const docs = data ?? [];
    return {
      platform: docs.filter((d) => d.federation_id === null),
      federation: docs.filter((d) => d.federation_id !== null),
    };
  }, [data]);

  const isEmpty = !isLoading && !isError && (data ?? []).length === 0;

  return (
    <>
      <PageHeader
        title={t('documents.title')}
        sub={t('documents.subtitle')}
        actions={
          isAdmin ? (
            <Button variant="accent" size="sm" onClick={() => setUploadOpen(true)}>
              <Icon.upload className="h-3.5 w-3.5" /> {t('documents.upload')}
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : isError && !data ? (
        <div className="rounded border border-dashed border-danger bg-danger-tint px-6 py-12 text-center">
          <p className="text-sm text-danger">{t('documents.couldNotLoad')}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-xs font-medium text-ink underline underline-offset-2 hover:text-ink-2"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      ) : isEmpty ? (
        <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-16 text-center">
          <Icon.doc className="h-7 w-7 text-muted" />
          <p className="mt-3 text-sm font-medium text-ink">{t('documents.noDocumentsTitle')}</p>
          <p className="mt-1 text-xs text-muted">
            {isAdmin ? t('documents.noDocumentsAdmin') : t('documents.noDocumentsReferee')}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {platform.length > 0 && (
            <DocumentGroup
              title={t('documents.official')}
              sub={t('documents.officialSub')}
              docs={platform}
              isAdmin={isAdmin}
              canBookmark={canBookmark}
            />
          )}
          {federation.length > 0 && (
            <DocumentGroup
              title={t('documents.yourFederation')}
              sub={t('documents.federationSub')}
              docs={federation}
              isAdmin={isAdmin}
              canBookmark={canBookmark}
            />
          )}
        </div>
      )}

      {isAdmin && <UploadDocumentDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />}
    </>
  );
}

function DocumentGroup({
  title,
  sub,
  docs,
  isAdmin,
  canBookmark,
}: {
  title: string;
  sub: string;
  docs: Document[];
  isAdmin: boolean;
  canBookmark: boolean;
}) {
  return (
    <section>
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <p className="text-xs text-muted">{sub}</p>
      </div>
      <div className="space-y-2">
        {docs.map((doc) => (
          <DocumentRow key={doc.id} doc={doc} isAdmin={isAdmin} canBookmark={canBookmark} />
        ))}
      </div>
    </section>
  );
}

function DocumentRow({ doc, isAdmin, canBookmark }: { doc: Document; isAdmin: boolean; canBookmark: boolean }) {
  const { t } = useTranslation();
  const download = useDownloadDocument();
  const remove = useDeleteDocument();
  const toggleBookmark = useToggleDocumentBookmark();
  const meta = typeMeta(t)[doc.type];
  const isSaved = doc.is_bookmarked ?? false;

  async function onToggleBookmark() {
    try {
      await toggleBookmark.mutateAsync({ id: doc.id, isBookmarked: isSaved });
    } catch (err) {
      // Optimistic flip already rolled back in the hook; surface the failure.
      notifyError(err, isSaved ? t('documents.bookmarkRemoveErrorToast') : t('documents.bookmarkSaveErrorToast'));
    }
  }

  async function onDownload() {
    try {
      const url = await download.mutateAsync(doc.id);
      // Open in a new tab — the browser handles the PDF/DOCX (view or save). The signed URL is
      // short-lived + single-purpose, so it's resolved per click rather than cached.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      notifyError(err, t('documents.openErrorToast'));
    }
  }

  async function onDelete() {
    try {
      await remove.mutateAsync(doc.id);
      notifySuccess(t('documents.deletedToast'));
    } catch (err) {
      notifyError(err, t('documents.deleteErrorToast'));
    }
  }

  return (
    <div className="flex items-center gap-3 rounded border border-line-2 bg-surface p-3 shadow-1 transition-colors hover:border-line-3">
      <span className="grid h-10 w-10 flex-none place-items-center rounded-sm bg-bg-2 text-ink-3">
        <Icon.doc className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="line-clamp-1 text-sm font-medium text-ink">{doc.title}</div>
        <div className="mt-1 flex items-center gap-2 text-[11.5px] text-muted">
          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-medium ${meta.tint}`}>{meta.label}</span>
          <span className="uppercase">{doc.file_format}</span>
          {doc.file_size_bytes != null && <span>· {formatSize(doc.file_size_bytes)}</span>}
        </div>
      </div>

      <div className="flex flex-none items-center gap-1.5">
        {canBookmark && (
          <button
            type="button"
            onClick={onToggleBookmark}
            aria-label={isSaved ? t('documents.unsaveAria', { title: doc.title }) : t('documents.saveAria', { title: doc.title })}
            aria-pressed={isSaved}
            className={
              'grid h-8 w-8 place-items-center rounded-btn transition-colors ' +
              (isSaved ? 'text-accent hover:text-accent-2' : 'text-muted hover:text-ink')
            }
          >
            <Icon.bookmark className={'h-4 w-4 ' + (isSaved ? 'fill-current' : '')} />
          </button>
        )}
        <Button variant="default" size="sm" onClick={onDownload} disabled={download.isPending}>
          <Icon.arrowDown className="h-3.5 w-3.5" /> {t('documents.download')}
        </Button>
        {isAdmin && (
          <button
            type="button"
            onClick={onDelete}
            disabled={remove.isPending}
            aria-label={t('documents.deleteAria', { title: doc.title })}
            className="grid h-8 w-8 place-items-center rounded-btn text-muted transition-colors hover:text-danger disabled:opacity-50"
          >
            <Icon.trash className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
